import {
  Badge,
  IconPopover,
  KeyReferenceBar,
  KeyReferenceBarSection,
  Skeleton,
} from "@apex/component-library";
import { Box, Link, Text } from "@chakra-ui/react";
import startCase from "lodash/startCase";
import toLower from "lodash/toLower";
import React from "react";

import { AccountData } from "../../api/useGetAccountDetails";
import { useGetCashBalances } from "../../api/useGetCashBalances";
import { useGetMargins } from "../../api/useGetMargins";
import { useAccountLookupStore } from "../../hooks/useAccountLookupStore";
import { Status } from "../../types";
import { formatSummaryValues } from "../../utils/numberFormatter";

const badgeColorOptions: Record<Status, BadgeColorScheme> = {
  OPEN: "green",
  CLOSED: "red",
  "": "gray",
};

type Props = {
  accountData: AccountData;
  isLoadingAccount: boolean;
  hasAccountErr: boolean;
};

const AccountDetailBar = ({
  accountData,
  isLoadingAccount,
  hasAccountErr,
}: Props) => {
  const accountNumber = useAccountLookupStore(state => state.accountNumber);

  const { data: cashBalanceData, isLoading: cashBalanceIsLoading } =
    useGetCashBalances(accountNumber || "");

  const { data: marginData, isLoading: marginIsLoading } = useGetMargins(
    accountNumber || ""
  );
  const status = accountData?.isActive ? "OPEN" : "CLOSED";

  const isLoading = isLoadingAccount || marginIsLoading || cashBalanceIsLoading;

  return (
    <KeyReferenceBar w="100%" pl={4} mt={0}>
      <Skeleton isLoaded={!isLoading} display="flex">
        <KeyReferenceBarSection title="Status" data-testid="state-title">
          <DefaultValueOrError
            value={
              <Badge
                data-testid="state-value"
                isInline
                colorScheme={getBadgeColor(
                  accountData?.isActive || false,
                  badgeColorOptions
                )}
              >
                {status}
              </Badge>
            }
            hasError={hasAccountErr}
          />
        </KeyReferenceBarSection>
      </Skeleton>
      <Skeleton isLoaded={!isLoading} display="flex">
        <KeyReferenceBarSection
          title="Account ID"
          data-testid="account-id-title"
        >
          <Box data-testid="account-id-value">
            <DefaultValueOrError
              value={
                <Link
                  color="apex.link"
                  href={`/accounts/#/${cashBalanceData?.account}`}
                >
                  {cashBalanceData?.account}
                </Link>
              }
              hasError={hasAccountErr}
              hasValue={!!cashBalanceData?.account}
            />
          </Box>
        </KeyReferenceBarSection>
      </Skeleton>
      <Skeleton isLoaded={!isLoading} display="flex">
        <KeyReferenceBarSection title="Title" data-testid="title-title">
          <Box data-testid="title-value">
            <DefaultValueOrError
              value={accountData?.accountTitle}
              hasError={hasAccountErr}
            />
          </Box>
        </KeyReferenceBarSection>
      </Skeleton>
      <Skeleton isLoaded={!isLoading} display="flex">
        <KeyReferenceBarSection
          title="Funding Type"
          data-testid="funding-type-title"
        >
          <Box data-testid="funding-type-value">
            <DefaultValueOrError
              value={startCase(
                toLower(accountData?.accountType || "Unavailable")
              )}
              hasError={hasAccountErr}
            />
          </Box>
        </KeyReferenceBarSection>
      </Skeleton>
      <Skeleton isLoaded={!isLoading} display="flex">
        <KeyReferenceBarSection
          title="Account Equity"
          data-testid="account-equity-title"
        >
          <Box data-testid="account-equity-value">
            <DefaultValueOrError
              value={formatSummaryValues(marginData?.liquidatingEquity)}
              hasError={hasAccountErr}
            />
          </Box>
        </KeyReferenceBarSection>
      </Skeleton>
      <Skeleton isLoaded={!isLoading} display="flex">
        <KeyReferenceBarSection
          title="Amount Available"
          data-testid="amount-available-title"
          icon={
            <IconPopover
              infoIconVariant="subtle"
              bodyContent={
                <Text>
                  This includes adjustments to the start of day balance for
                  recent deposits, intraday activities, and pending debit
                  interest and dividends.
                </Text>
              }
            />
          }
        >
          <Box data-testid="amount-available-value">
            <DefaultValueOrError
              value={formatSummaryValues(
                cashBalanceData?.balanceDetails?.amountAvailableToWithdraw
              )}
              hasError={hasAccountErr}
            />
          </Box>
        </KeyReferenceBarSection>
      </Skeleton>
      <Skeleton isLoaded={!isLoading} display="flex">
        <KeyReferenceBarSection
          title="Buying Power"
          data-testid="buying-power-title"
        >
          <Box data-testid="buying-power-value">
            <DefaultValueOrError
              value={formatSummaryValues(marginData?.overnightBuyingPower)}
              hasError={hasAccountErr}
            />
          </Box>
        </KeyReferenceBarSection>
      </Skeleton>
    </KeyReferenceBar>
  );
};

export default AccountDetailBar;

type DefaultValueProps = {
  value?: React.ReactNode;
  hasError?: boolean;
  hasValue?: boolean;
};

function DefaultValueOrError({
  value,
  hasValue = true,
  hasError,
}: DefaultValueProps) {
  if (hasError) return <InlineError />;
  if (!hasValue || !value) return <></>;
  return <>{value}</>;
}

function InlineError({ errorMessage = "Error" }) {
  return (
    <Text as="em" color="red.500">
      {errorMessage}
    </Text>
  );
}
type BadgeColorScheme = "red" | "green" | "yellow" | "blue" | "gray";
function getBadgeColor<TValue extends string>(
  value: boolean,
  colorConfig: Record<TValue, BadgeColorScheme>
): BadgeColorScheme | undefined {
  const state = value ? "OPEN" : "CLOSED";
  return colorConfig[state as keyof typeof colorConfig];
}
